import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Pencil } from "@phosphor-icons/react";

const CATS = [
  ["kamar", "Kamar"],
  ["kelas", "Kelas"],
  ["halaqah", "Halaqah"],
  ["divisi", "Divisi"],
  ["pelanggaran", "Jenis Pelanggaran (Poin)"],
  ["kegiatan", "Daftar Kegiatan"],
  ["absensi_type", "Jenis Absensi"],
  ["perizinan", "Jenis Perizinan"],
  ["predikat_tahfiz", "Predikat Tahfiz"],
  ["bobot_tahfiz", "Bobot Nilai Tahfiz"],
  ["jadwal_kegiatan", "Jadwal Kegiatan"],
];

const DIVISI_OPTIONS = ["keibadahan", "lugho", "kedisiplinan", "kebersihan", "keamanan", "kesehatan", "humas", "logistik"];
const HARI_OPTIONS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Ahad"];

const FIELDS = {
  kamar: ["nama", "kapasitas"],
  kelas: ["nama", "tingkat"],
  halaqah: ["nama"],
  divisi: ["nama", "kode"],
  pelanggaran: ["nama", "divisi", "kategori", "poin"],
  kegiatan: ["nama", "divisi", "deskripsi"],
  absensi_type: ["nama"],
  perizinan: ["nama"],
  predikat_tahfiz: ["nama", "min_nilai", "max_nilai"],
  bobot_tahfiz: ["nama", "bobot"],
  jadwal_kegiatan: ["nama", "hari", "divisi", "jam_mulai", "jam_selesai", "lokasi"],
};

export default function Pengaturan() {
  const [cat, setCat] = useState("kamar");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get(`/master/${cat}`); setItems(data); };
  useEffect(() => { load(); setForm({}); setEditing(null); /* eslint-disable-next-line */ }, [cat]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const cleaned = {};
      for (const k of FIELDS[cat]) {
        if (form[k] === "" || form[k] === undefined) continue;
        if (["poin", "kapasitas"].includes(k)) cleaned[k] = parseInt(form[k], 10);
        else if (["min_nilai", "max_nilai", "bobot"].includes(k)) cleaned[k] = parseFloat(form[k]);
        else cleaned[k] = form[k];
      }
      if (editing) await api.put(`/master/${cat}/${editing.id}`, cleaned);
      else await api.post(`/master/${cat}`, cleaned);
      toast.success("Tersimpan"); setForm({}); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/master/${cat}/${id}`); load(); };
  const openEdit = (it) => { setEditing(it); const f = {}; for (const k of FIELDS[cat]) f[k] = it[k]; setForm(f); };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Master Data</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Pengaturan</h1>
      </div>

      <div className="imsyaf-card p-4 flex flex-wrap gap-2">
        {CATS.map(([k, l]) => (
          <button key={k} onClick={() => setCat(k)} data-testid={`cat-${k}`}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${cat === k ? "bg-[#14532d] text-white" : "text-slate-700 hover:bg-slate-100 border border-slate-200"}`}>{l}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <form onSubmit={submit} className="imsyaf-card p-5 md:col-span-1 space-y-3">
          <h3 className="font-display text-lg font-semibold text-[#064E3B]">{editing ? "Edit" : "Tambah"} — {CATS.find((c) => c[0] === cat)[1]}</h3>
          {FIELDS[cat].map((f) => (
            <div key={f}>
              <label className="text-xs capitalize">{f.replace(/_/g, " ")}</label>
              {f === "divisi" ? (
                <select className="imsyaf-input" value={form[f] ?? ""}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  data-testid={`master-${cat}-${f}`}>
                  <option value="">— Pilih Divisi —</option>
                  {DIVISI_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              ) : f === "hari" ? (
                <select className="imsyaf-input" value={form[f] ?? ""}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  data-testid={`master-${cat}-${f}`}>
                  <option value="">— Pilih Hari —</option>
                  {HARI_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              ) : (
                <input className="imsyaf-input" type={["poin", "kapasitas", "min_nilai", "max_nilai", "bobot"].includes(f) ? "number" : (f.startsWith("jam") ? "time" : "text")}
                  step={["min_nilai", "max_nilai", "bobot"].includes(f) ? "0.01" : "1"}
                  value={form[f] ?? ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  data-testid={`master-${cat}-${f}`} />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <button className="imsyaf-btn-primary flex-1" data-testid="master-save-btn"><Plus size={16}/> Simpan</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm({}); }} className="imsyaf-btn-outline">Batal</button>}
          </div>
        </form>

        <div className="imsyaf-card md:col-span-2 overflow-hidden">
          <table className="w-full text-sm zebra">
            <thead className="bg-slate-50"><tr>
              {FIELDS[cat].map((f) => <th key={f} className="text-left px-4 py-2 font-semibold capitalize">{f.replace(/_/g, " ")}</th>)}
              <th></th>
            </tr></thead>
            <tbody>{items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100">
                {FIELDS[cat].map((f) => <td key={f} className="px-4 py-2">{it[f] ?? "-"}</td>)}
                <td className="px-4 py-2 text-right">
                  <button className="imsyaf-btn-ghost" onClick={() => openEdit(it)}><Pencil size={14}/></button>
                  <button className="imsyaf-btn-ghost text-red-600" onClick={() => del(it.id)}><Trash size={14}/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={FIELDS[cat].length + 1} className="text-center py-8 text-slate-400">Belum ada data.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
